import { useEffect, useState } from 'react';
import { FileDown, Search, RefreshCw, Filter } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import api from '../services/api';

const statusLabel = {
  NUEVO: 'Nuevo', ASIGNADO: 'Asignado', EN_PROCESO: 'En proceso',
  EN_ESPERA: 'En espera', COMPLETADO: 'Completado', CANCELADO: 'Cancelado'
};
const statusColor = {
  NUEVO: { bg: '#eff6ff', text: '#2563eb' }, ASIGNADO: { bg: '#fffbeb', text: '#d97706' },
  EN_PROCESO: { bg: '#fff7ed', text: '#ea580c' }, EN_ESPERA: { bg: '#f5f3ff', text: '#7c3aed' },
  COMPLETADO: { bg: '#f0fdf4', text: '#16a34a' }, CANCELADO: { bg: '#fef2f2', text: '#dc2626' },
};
const priorityColor = {
  BAJA: { bg: '#f1f5f9', text: '#64748b' }, MEDIA: { bg: '#eff6ff', text: '#2563eb' },
  ALTA: { bg: '#fff7ed', text: '#ea580c' }, CRITICA: { bg: '#fef2f2', text: '#dc2626' },
};
const emptyFilters = { status: '', areaId: '', priority: '', assignedToId: '', from: '', to: '' };

export default function ReportesPage() {
  const [data, setData] = useState([]);
  const [areas, setAreas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    api.get('/areas').then(r => setAreas(r.data)).catch(() => {});
    api.get('/auth/users').then(r => setUsuarios(r.data.filter(u => u.active))).catch(() => {});
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.areaId) params.areaId = filters.areaId;
      if (filters.priority) params.priority = filters.priority;
      if (filters.assignedToId) params.assignedToId = filters.assignedToId;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const res = await api.get('/dashboard/report', { params });
      setData(res.data.data);
      setSearched(true);
    } catch { alert('Error al cargar el reporte'); }
    finally { setLoading(false); }
  };

  const handleReset = () => { setFilters(emptyFilters); setData([]); setSearched(false); };

  const exportToExcel = () => {
    if (data.length === 0) return;
    const rows = data.map(r => ({
      'Folio': r.folio, 'Descripción': r.descripcion, 'Solicitante': r.solicitante,
      'Área': r.area, 'Prioridad': r.prioridad, 'Estatus': statusLabel[r.estatus] || r.estatus,
      'Creado por': r.creadoPor, 'Asignado a': r.asignadoA,
      'Fecha creación': r.fechaCreacion ? new Date(r.fechaCreacion).toLocaleString('es-MX') : '',
      'Fecha cierre': r.fechaCierre ? new Date(r.fechaCierre).toLocaleString('es-MX') : '',
      'Tiempo atención (horas)': r.tiempoAtencionHoras ?? '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    ws['!cols'] = [{ wch: 16 }, { wch: 40 }, { wch: 24 }, { wch: 22 }, { wch: 12 }, { wch: 14 }, { wch: 24 }, { wch: 24 }, { wch: 22 }, { wch: 22 }, { wch: 22 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `ServiceFlow_Reporte_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      <style>{`
        .rep-filter-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        .rep-actions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
        @media (max-width: 768px) {
          .rep-filter-grid { grid-template-columns: 1fr 1fr !important; }
          .rep-table { display: none !important; }
          .rep-cards { display: block !important; }
        }
        .rep-cards { display: none; }
      `}</style>

      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2f6b', margin: 0 }}>Reportes</h1>
        <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Consulta y exporta solicitudes con filtros avanzados</p>
      </div>

      {/* Filtros */}
      <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
          <Filter size={14} color="#2563eb" />
          <span style={{ fontWeight: 700, color: '#1a2f6b', fontSize: 14 }}>Filtros</span>
        </div>

        <div className="rep-filter-grid" style={{ marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Estatus</label>
            <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}
              style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
              <option value="">Todos</option>
              {Object.entries(statusLabel).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Área</label>
            <select value={filters.areaId} onChange={e => setFilters({ ...filters, areaId: e.target.value })}
              style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
              <option value="">Todas</option>
              {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Prioridad</label>
            <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}
              style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
              <option value="">Todas</option>
              {['BAJA', 'MEDIA', 'ALTA', 'CRITICA'].map(p => <option key={p} value={p}>{p.charAt(0) + p.slice(1).toLowerCase()}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Responsable</label>
            <select value={filters.assignedToId} onChange={e => setFilters({ ...filters, assignedToId: e.target.value })}
              style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
              <option value="">Todos</option>
              {usuarios.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Desde</label>
            <input type="date" value={filters.from} onChange={e => setFilters({ ...filters, from: e.target.value })}
              style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Hasta</label>
            <input type="date" value={filters.to} onChange={e => setFilters({ ...filters, to: e.target.value })}
              style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
          </div>
        </div>

        <div className="rep-actions">
          <button onClick={fetchReport} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2563eb', color: 'white', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            {loading ? <RefreshCw size={14} /> : <Search size={14} />}
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
          <button onClick={handleReset}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'white', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            <RefreshCw size={14} /> Limpiar
          </button>
          {data.length > 0 && (
            <button onClick={exportToExcel}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#16a34a', color: 'white', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              <FileDown size={14} /> Excel ({data.length})
            </button>
          )}
        </div>
      </div>

      {/* Resultados */}
      {searched && (
        <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, color: '#1a2f6b', fontSize: 14 }}>
              Resultados
              <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700, marginLeft: 8 }}>{data.length}</span>
            </span>
          </div>

          {/* Tabla desktop */}
          <div style={{ overflowX: 'auto' }}>
            <table className="rep-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Folio', 'Descripción', 'Área', 'Prioridad', 'Estatus', 'Asignado a', 'Tiempo', 'Fecha'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>Sin resultados</td></tr>
                ) : data.map((r, i) => {
                  const sc = statusColor[r.estatus] || {};
                  const pc = priorityColor[r.prioridad] || {};
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    >
                      <td style={{ padding: '10px 14px' }}><span style={{ fontFamily: 'monospace', fontSize: 10, color: '#2563eb', fontWeight: 700 }}>{r.folio}</span></td>
                      <td style={{ padding: '10px 14px', maxWidth: 220 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.descripcion}</div></td>
                      <td style={{ padding: '10px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.area}</td>
                      <td style={{ padding: '10px 14px' }}><span style={{ background: pc.bg, color: pc.text, padding: '2px 6px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{r.prioridad}</span></td>
                      <td style={{ padding: '10px 14px' }}><span style={{ background: sc.bg, color: sc.text, padding: '2px 6px', borderRadius: 20, fontSize: 10, fontWeight: 600 }}>{statusLabel[r.estatus] || r.estatus}</span></td>
                      <td style={{ padding: '10px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.asignadoA}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        {r.tiempoAtencionHoras != null
                          ? <span style={{ background: r.tiempoAtencionHoras > 72 ? '#fef2f2' : '#f0fdf4', color: r.tiempoAtencionHoras > 72 ? '#dc2626' : '#16a34a', padding: '2px 6px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{r.tiempoAtencionHoras}h</span>
                          : <span style={{ color: '#94a3b8' }}>—</span>}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 10, whiteSpace: 'nowrap' }}>{new Date(r.fechaCreacion).toLocaleDateString('es-MX')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Tarjetas móvil */}
          <div className="rep-cards" style={{ padding: 12 }}>
            {data.length === 0 && <p style={{ textAlign: 'center', color: '#94a3b8' }}>Sin resultados</p>}
            {data.map((r, i) => {
              const sc = statusColor[r.estatus] || {};
              const pc = priorityColor[r.prioridad] || {};
              return (
                <div key={i} style={{ background: '#f8fafc', borderRadius: 12, padding: 14, marginBottom: 10, border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#2563eb', fontWeight: 700 }}>{r.folio}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <span style={{ background: pc.bg, color: pc.text, padding: '2px 6px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{r.prioridad}</span>
                      <span style={{ background: sc.bg, color: sc.text, padding: '2px 6px', borderRadius: 20, fontSize: 10, fontWeight: 600 }}>{statusLabel[r.estatus] || r.estatus}</span>
                    </div>
                  </div>
                  <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{r.descripcion}</p>
                  <div style={{ fontSize: 11, color: '#64748b', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <span>🏢 {r.area}</span>
                    <span>👤 {r.asignadoA}</span>
                    {r.tiempoAtencionHoras != null && <span>⏱ {r.tiempoAtencionHoras}h</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!searched && !loading && (
        <div style={{ textAlign: 'center', padding: '40px 16px', color: '#94a3b8' }}>
          <Filter size={32} color="#e2e8f0" style={{ margin: '0 auto 10px', display: 'block' }} />
          <p style={{ margin: 0, fontSize: 13 }}>Selecciona filtros y haz clic en <strong>Buscar</strong></p>
        </div>
      )}
    </div>
  );
}
